import {
    IonContent,
    IonPage,
    useIonViewWillEnter,
    IonToast,
    IonSearchbar,
    IonSpinner
} from '@ionic/react';

import * as React from 'react';
import Header from '../../components/Header/Header';

import {useEffect, useState} from 'react';
import firebaseService from '../../services/firebaseService';
import {Camera} from '../../interfaces/Camera';
import tourService from "../../services/tourService";
import Tour from "reactour";
import './Live.css';
import Map from '../../components/Maps/Map'

import CctvOverlayPanel from '../../components/CctvPlayer/CctvOverlayPanel';
import {useHistory} from "react-router";

interface LiveProps {
    /** Opened from the Work Zones tile: start with that layer drawn. */
    focus?: 'workzones';
}

const Live: React.FC<LiveProps> = ({focus}: LiveProps) => {
    const [displayedCCTVs, setDisplayedCCTVs] = useState<Camera[]>([]);
    // Road conditions over the camera map: what a camera is showing usually
    // makes sense only next to how the road it points at is running.
    const [showProbe, setShowProbe] = useState(false);
    const [showWorkzones, setShowWorkzones] = useState(focus === 'workzones');
    const [showCrashes, setShowCrashes] = useState(false);
    const [showLiveCrashes, setShowLiveCrashes] = useState(false);
    const [showWeather, setShowWeather] = useState(false);
    const [cameras, setCameras] = useState<Camera[]>([]);
    const [allCameras, setAllCameras] = useState<Camera[]>(cameras);
    const [showLoading, setShowLoading] = useState(false);
    const [errorResponse, setErrorResponse] = useState(false);
    const [searchValue, setSearchValue] = useState('');

    useEffect(() => {
        firebaseService.getCams().get().then((docs) => {
            const foundCameras: Camera[] = [];
            docs.forEach(doc => {
                const cam = doc.data() as Camera
                if( cam.latitude != 0 && cam.longitude != 0 && Number.isFinite(cam.latitude)&& Number.isFinite(cam.longitude))
                    foundCameras.push(cam);
            });
            setCameras(foundCameras);
            setAllCameras(foundCameras);
        });
    }, []);

    const setNewCamera = (id: number) => {
        const data = cameras.find(camera => camera.id === id);
        if (!data) {
            return;
        }
        // Newest first, and clicking an already-open camera lifts it back to
        // the top rather than doing nothing.
        setDisplayedCCTVs(open => [data as Camera, ...open.filter(camera => camera.id !== id)]);
    };

    const closeCurrentCamera = (id: number) => {
        setDisplayedCCTVs(displayedCCTVs.filter(camera => camera.id !== id));
    };

    const cameraError = (error: string, id: number) => {
        setErrorResponse(true);
        setDisplayedCCTVs(displayedCCTVs.filter(camera => camera.id !== id));
    };

    function search(e: CustomEvent) {
        const searchText = e.detail?.value;
        setSearchValue(searchText);
        let filteredCameras = cameras;
        if (searchText !== '') {
            filteredCameras = filteredCameras.filter((camera) => {
                return camera.description.toLowerCase().includes(searchText.toLowerCase());
            });
            setCameras(filteredCameras);
        } else {
            setCameras(allCameras);
        }
    }

    function clearAllSelectedCameras(){
        setDisplayedCCTVs([]);
    }

    useIonViewWillEnter( () => {
        setShowLoading(true);
    });
    const steps = tourService.getStepsFor('Live');
    const isTour = tourService.StartTour();
    const history = useHistory();

    return (
        <IonPage>
            <Header title={focus === 'workzones' ? 'Work zones' : 'Live stream'} />
            <IonContent fullscreen className="cctv-content">
                {/* The map is the page: it fills the content area and every
                    control floats over it. */}
                <div className="cctv-stage cctv-stage--full first-step">
                    <Map height="100%" traffic={true} location={true} cctv={true} cameras={cameras}
                         stateOutline={true} probe={showProbe} workzones={showWorkzones}
                         crashes={showCrashes} liveCrashes={showLiveCrashes} weather={showWeather}
                         setId={setNewCamera} setShowLoading={setShowLoading}/>

                    <div className="cctv-toolbar">
                        <IonSearchbar
                            className="live-search"
                            placeholder="Search cameras"
                            value={searchValue}
                            onIonChange={search}
                        />
                        <span className="cctv-chip">
                            {cameras.length} camera{cameras.length === 1 ? '' : 's'}
                        </span>
                    </div>

                    <div className="live-layers" role="group" aria-label="Map layers">
                        <h2 className="live-layers__title">Layers</h2>
                        <label className="live-layer">
                            <input type="checkbox" checked={showProbe}
                                   onChange={() => setShowProbe(on => !on)} />
                            <span>
                                <span className="live-layer__label">Probe speeds</span>
                                <span className="live-layer__hint">Live jam factor by segment</span>
                            </span>
                        </label>
                        <label className="live-layer">
                            <input type="checkbox" checked={showWorkzones}
                                   onChange={() => setShowWorkzones(on => !on)} />
                            <span>
                                <span className="live-layer__label">Work zones</span>
                                <span className="live-layer__hint">Active WZDX work zones</span>
                            </span>
                        </label>
                        <label className="live-layer">
                            <input type="checkbox" checked={showLiveCrashes}
                                   onChange={() => setShowLiveCrashes(on => !on)} />
                            <span>
                                <span className="live-layer__label">Crashes</span>
                                <span className="live-layer__hint">Open in the ATMS feed right now</span>
                            </span>
                        </label>
                        <label className="live-layer">
                            <input type="checkbox" checked={showCrashes}
                                   onChange={() => setShowCrashes(on => !on)} />
                            <span>
                                <span className="live-layer__label">Crash hotspots</span>
                                <span className="live-layer__hint">Five years of crashes; zoom in for each one</span>
                            </span>
                        </label>
                        <label className="live-layer">
                            <input type="checkbox" checked={showWeather}
                                   onChange={() => setShowWeather(on => !on)} />
                            <span>
                                <span className="live-layer__label">Weather</span>
                                <span className="live-layer__hint">Current conditions by county</span>
                            </span>
                        </label>
                    </div>

                    {displayedCCTVs.length === 0 && !showLoading && (
                        <p className="cctv-hint">Click a camera on the map to open its live feed</p>
                    )}

                    {showLoading && (
                        <div className="cctv-veil">
                            {/* A bare spinner: Loading renders a whole IonPage,
                                which cannot nest inside the map stage. */}
                            <IonSpinner name="crescent" className="cctv-veil__spinner" />
                            <p className="cctv-veil__text">Loading the map - this may take a moment</p>
                        </div>
                    )}

                    <CctvOverlayPanel
                        className="second-step"
                        cameras={displayedCCTVs}
                        onRemove={closeCurrentCamera}
                        onCloseAll={clearAllSelectedCameras}
                        onError={cameraError}
                    />
                </div>
            </IonContent>
            <IonToast
                isOpen={errorResponse}
                onDidDismiss={() => setErrorResponse(false)}
                color="danger"
                message="Could not connect to camera."
                cssClass="toast-error"
                duration={10000}
            />
            <Tour
                steps={steps}
                isOpen={isTour}
                accentColor='black'
                onRequestClose={() => {tourService.GoBack(history)}}/>
        </IonPage>
    )
};

export default Live;
